import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form-latest';

import { _notNil } from '@/littledash';

import Button from '@/components/UI/Button';

import type { NumericTableColumnAddProps } from './AddColumn.model';
import { uniqueNameValidatorFactory, valueIsNumeric } from './AddColumn.util';

interface NumericTableAddColumnFormData {
  name: string;
}

export const NumericTableColumnAdd: FC<Omit<NumericTableColumnAddProps, 'tableType'>> = ({
  tableUnit,
  toggleDropdownMenu,
  handleAddColumn,
  columns,
}) => {
  const defaultValues: NumericTableAddColumnFormData = { name: '' };
  const uniqueColumnName = useMemo(
    () => uniqueNameValidatorFactory(new Set(columns.map(({ name }) => name ?? ''))),
    [columns]
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<NumericTableAddColumnFormData>({
    mode: 'onChange',
    defaultValues,
  });

  const onSubmit = async ({ name }: NumericTableAddColumnFormData) => {
    try {
      await handleAddColumn({ type: 'number', unit: null, name: name?.trim() ?? null });
    } catch {
      // Error handling is responsibility of caller
    } finally {
      toggleDropdownMenu?.(false);
      reset(defaultValues);
    }
  };

  return (
    <div className="pa3" data-test-component="NumericTableColumnAdd">
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div>
          <div className="flex relative">
            <input
              type="text"
              autoComplete="off"
              placeholder="Name"
              data-test-element="name-input"
              data-testid="name-input"
              className={cn({ input__error: _notNil(errors.name) })}
              style={{ marginBottom: 0 }}
              autoFocus
              {...register('name', {
                required: 'This field is required',
                validate: { valueIsNumeric, uniqueColumnName },
              })}
            />
            <span className="ib w2 absolute right-1 tr" style={{ top: '25%' }}>
              <small data-testid="table-unit">{tableUnit}</small>
            </span>
          </div>
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => (
              <small className="red db pt2" data-testid="name-error">
                {message}
              </small>
            )}
          />
        </div>
        <div className="mt3">
          <Button
            stateless
            className="link db pl0"
            submit
            disabled={isSubmitting || !isValid}
            data-test-element="add-column-btn"
            testId="add-column-btn"
          >
            Add column
          </Button>
          <Button
            stateless
            className="near-black db pl0 mt2"
            testId="cancel-btn"
            onClick={() => {
              toggleDropdownMenu?.(false);
              reset(defaultValues);
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};
